import { BrowserType } from '../hooks/useBrowserDetection';

export interface BrowserHelpItem {
  browser: BrowserType;
  displayName: string;
  steps: string[];
  settingsUrl?: string;
  notes?: string[];
}

export interface CommonIssue {
  question: string;
  answer: string;
}

// Camera Help
export const CAMERA_HELP: Record<BrowserType, BrowserHelpItem> = {
  chrome: {
    browser: 'chrome',
    displayName: 'Google Chrome',
    steps: [
      'Click the 🔒 lock icon in the address bar (left side)',
      'Find "Camera" in the permissions list',
      'Select "Allow" from the dropdown',
      'Click the "Refresh" button below to retry',
    ],
    settingsUrl: 'chrome://settings/content/camera',
    notes: [
      'If blocked, you may need to refresh the page after allowing',
      'Check if another app is using your camera (Zoom, Teams, etc.)',
    ],
  },
  firefox: {
    browser: 'firefox',
    displayName: 'Mozilla Firefox',
    steps: [
      'Click the 🔒 lock icon in the address bar',
      'Click the arrow next to "Connection secure"',
      'Click "More Information"',
      'Go to "Permissions" tab',
      'Find "Use the Camera" and select "Allow"',
    ],
    settingsUrl: 'about:preferences#privacy',
    notes: [
      'Firefox may ask for permission each time',
      'Make sure to select the correct camera if you have multiple',
    ],
  },
  edge: {
    browser: 'edge',
    displayName: 'Microsoft Edge',
    steps: [
      'Click the 🔒 lock icon in the address bar',
      'Find "Camera" in the site permissions',
      'Select "Allow"',
      'Refresh the page if needed',
    ],
    settingsUrl: 'edge://settings/content/camera',
    notes: [
      'Edge permissions work similar to Chrome',
      'Check Windows privacy settings if camera is still blocked',
    ],
  },
  safari: {
    browser: 'safari',
    displayName: 'Safari',
    steps: [
      'Go to Safari menu → Preferences (or Settings)',
      'Click on "Websites" tab',
      'Select "Camera" from the left sidebar',
      'Find this website and select "Allow"',
    ],
    notes: [
      'Safari may require additional macOS permissions',
      'Go to System Preferences → Security & Privacy → Camera',
    ],
  },
  opera: {
    browser: 'opera',
    displayName: 'Opera',
    steps: [
      'Click the 🔒 lock icon in the address bar',
      'Find "Camera" in the permissions',
      'Select "Allow"',
      'Refresh the page',
    ],
    settingsUrl: 'opera://settings/content/camera',
  },
  unknown: {
    browser: 'unknown',
    displayName: 'Your Browser',
    steps: [
      'Look for a camera or lock icon in your address bar',
      'Click it and find camera permissions',
      'Allow camera access for this site',
      'Refresh the page if needed',
    ],
  },
};

// Microphone Help
export const MICROPHONE_HELP: Record<BrowserType, BrowserHelpItem> = {
  chrome: {
    browser: 'chrome',
    displayName: 'Google Chrome',
    steps: [
      'Click the 🔒 lock icon in the address bar',
      'Find "Microphone" in the permissions list',
      'Select "Allow" from the dropdown',
      'Click "Refresh" to retry',
    ],
    settingsUrl: 'chrome://settings/content/microphone',
  },
  firefox: {
    browser: 'firefox',
    displayName: 'Mozilla Firefox',
    steps: [
      'Click the 🔒 lock icon in the address bar',
      'Click the arrow next to "Connection secure"',
      'Click "More Information" → "Permissions" tab',
      'Find "Use the Microphone" and select "Allow"',
    ],
    settingsUrl: 'about:preferences#privacy',
  },
  edge: {
    browser: 'edge',
    displayName: 'Microsoft Edge',
    steps: [
      'Click the 🔒 lock icon in the address bar',
      'Find "Microphone" in site permissions',
      'Select "Allow"',
    ],
    settingsUrl: 'edge://settings/content/microphone',
  },
  safari: {
    browser: 'safari',
    displayName: 'Safari',
    steps: [
      'Go to Safari menu → Preferences',
      'Click "Websites" tab',
      'Select "Microphone" from sidebar',
      'Find this website and select "Allow"',
    ],
  },
  opera: {
    browser: 'opera',
    displayName: 'Opera',
    steps: [
      'Click the 🔒 lock icon in the address bar',
      'Find "Microphone" in permissions',
      'Select "Allow"',
    ],
    settingsUrl: 'opera://settings/content/microphone',
  },
  unknown: {
    browser: 'unknown',
    displayName: 'Your Browser',
    steps: [
      'Look for a microphone or lock icon in your address bar',
      'Click it and find microphone permissions',
      'Allow microphone access for this site',
    ],
  },
};

// Screen Share Help
export const SCREEN_SHARE_HELP: Record<BrowserType, BrowserHelpItem> = {
  chrome: {
    browser: 'chrome',
    displayName: 'Google Chrome',
    steps: [
      'When the dialog appears, click the "Entire Screen" tab (first tab)',
      'Select your screen thumbnail (Screen 1 or your primary monitor)',
      'Check the "Also share system audio" checkbox at the bottom',
      'Click "Share" button',
    ],
    notes: [
      'You MUST select "Entire Screen" - not "Window" or "Chrome Tab"',
      'System audio checkbox may not appear on Mac/Linux',
      'If you have multiple monitors, select your primary one',
    ],
  },
  firefox: {
    browser: 'firefox',
    displayName: 'Mozilla Firefox',
    steps: [
      'Click the dropdown that says "Select Window or Screen"',
      'Look for "Entire Screen" or "Full Screen" option',
      'Select your monitor from the list',
      'Click "Allow"',
    ],
    notes: [
      'Firefox may not support system audio sharing',
      'Make sure to select entire screen, not a specific window',
    ],
  },
  edge: {
    browser: 'edge',
    displayName: 'Microsoft Edge',
    steps: [
      'Click the "Entire Screen" tab in the share dialog',
      'Select your screen thumbnail',
      'Check "Share system audio" if available',
      'Click "Share"',
    ],
    notes: [
      'Edge works similar to Chrome',
      'System audio is supported on Windows',
    ],
  },
  safari: {
    browser: 'safari',
    displayName: 'Safari',
    steps: [
      'Safari will show a screen recording permission dialog',
      'Select "Entire Screen" option',
      'Allow screen recording in macOS System Preferences if prompted',
      'Go to System Preferences → Security & Privacy → Screen Recording',
    ],
    notes: [
      'Safari requires macOS screen recording permission',
      'System audio sharing is limited on Safari',
    ],
  },
  opera: {
    browser: 'opera',
    displayName: 'Opera',
    steps: [
      'Click "Entire Screen" tab in the dialog',
      'Select your screen',
      'Enable system audio if available',
      'Click "Share"',
    ],
  },
  unknown: {
    browser: 'unknown',
    displayName: 'Your Browser',
    steps: [
      'When prompted, look for "Entire Screen" or "Full Screen" option',
      'Do NOT select a specific window or browser tab',
      'Select your main monitor/screen',
      'Enable audio sharing if the option is available',
    ],
  },
};

// Common Camera Issues
export const CAMERA_ISSUES: CommonIssue[] = [
  {
    question: 'Camera shows a black screen',
    answer: 'Another app might be using your camera. Close Zoom, Teams, Skype, or other video apps and try again.',
  },
  {
    question: '"Camera not found" error',
    answer: 'Check if your webcam is properly connected. For laptops, check if there\'s a physical camera cover or privacy switch.',
  },
  {
    question: 'Camera permission was blocked',
    answer: 'Click the camera/lock icon in your address bar, select "Allow", then refresh the page.',
  },
  {
    question: 'Using external USB webcam?',
    answer: 'Make sure it\'s plugged in before opening this page. If just connected, refresh the page.',
  },
  {
    question: 'Camera showing wrong device',
    answer: 'If you have multiple cameras, check browser settings to select the correct one.',
  },
];

// Common Microphone Issues
export const MICROPHONE_ISSUES: CommonIssue[] = [
  {
    question: 'No audio level showing',
    answer: 'Check if microphone is muted in system settings. Windows: Right-click speaker icon → Sound settings. Mac: System Preferences → Sound → Input.',
  },
  {
    question: 'Speech not being recognized',
    answer: 'Ensure you\'re speaking clearly in English. Move closer to the microphone and minimize background noise.',
  },
  {
    question: 'Using Bluetooth headphones?',
    answer: 'Make sure they\'re connected and set as the input device. Some Bluetooth devices have separate mic permissions.',
  },
  {
    question: 'Wrong microphone selected?',
    answer: 'If you have multiple mics, the browser may have selected the wrong one. Check browser or system audio settings.',
  },
  {
    question: 'Microphone too quiet or too loud',
    answer: 'Adjust your microphone volume in system settings. Aim for the green zone in the audio level indicator.',
  },
];

// Common Screen Share Issues
export const SCREEN_SHARE_ISSUES: CommonIssue[] = [
  {
    question: '"Entire Screen" option not available',
    answer: 'Some corporate networks or browser extensions restrict screen sharing. Try using a personal device or disable extensions.',
  },
  {
    question: 'Selected wrong option (Window/Tab)',
    answer: 'Click "Try Again" and this time select the "Entire Screen" tab first, then choose your monitor.',
  },
  {
    question: 'Screen share keeps disconnecting',
    answer: 'Check your internet connection. Close bandwidth-heavy apps like video streaming services.',
  },
  {
    question: 'No system audio option showing',
    answer: 'System audio sharing requires Chrome or Edge on Windows/ChromeOS. Mac and Linux have limited support.',
  },
  {
    question: 'Black screen after sharing',
    answer: 'Some apps with DRM protection show as black. Close Netflix, Disney+, or other streaming apps and try again.',
  },
  {
    question: 'Multiple monitors - which to share?',
    answer: 'Share your PRIMARY monitor where this assessment will run. The dialog shows small previews to help identify screens.',
  },
];

// Speech Recognition Tips
export const SPEECH_TIPS: string[] = [
  'Speak clearly at a normal pace',
  'Stay 1-2 feet from the microphone',
  'Minimize background noise',
  'Use a headset with microphone for best results',
  'Avoid covering your mouth while speaking',
  'Speak in English for best recognition',
];
